import type { AdminUserWithClaimsResponse } from "@hellocashflow/domain";
import { useEffect, useMemo, useRef } from "react";

import {
  buildAdminAccessAuditSummary,
  formatDateTime,
} from "@/pages/adminAccessAuditHelpers";
import { useGetUsersWithClaimsQuery } from "@/store/api/baseApi";
import { getPublicErrorDetails, toError } from "@/utils/errorHandling";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import {
  VNextButton,
  VNextCard,
  VNextKeyValueList,
  VNextNotice,
  VNextStat,
} from "@/vnext/primitives";
import { AdminAccessAuditResultsTable } from "@/vnext/routes/settings/admin-access/AdminAccessAuditResultsTable";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";

import "@/vnext/routes/settings/admin-access/adminAccessAuditMake.css";

const logger = FrontendLoggerFactory.getLogger().withComponent(
  "AdminAccessAuditRoute",
);

const CLAIM_KEY = "admin";

const countEnabledClaimValues = (
  users: ReadonlyArray<AdminUserWithClaimsResponse["users"][number]>,
  claimKey: string,
) =>
  users.filter((user) => user.firebaseUser.customClaims[claimKey] === true)
    .length;

const getMetadataItems = (
  data: AdminUserWithClaimsResponse | undefined,
  userCount: number,
) => [
  {
    key: "claim-key",
    label: "Claim key",
    value: data?.claimKey ?? CLAIM_KEY,
  },
  {
    key: "scanned-at",
    label: "Last scanned",
    value: formatDateTime(data?.searchedAt),
  },
  {
    key: "processed",
    label: "Processed users",
    value:
      data?.totalUsersProcessed.toLocaleString() ?? userCount.toLocaleString(),
  },
];

export default function AdminAccessAuditRoute() {
  const pageStartedAtRef = useRef<number | null>(null);
  const readyLoggedRef = useRef(false);
  const { data, error, isFetching, isLoading, refetch } =
    useGetUsersWithClaimsQuery({
      firebaseClaimKey: CLAIM_KEY,
    });

  const adminUsers = useMemo(() => data?.users ?? [], [data?.users]);
  const summary = useMemo(
    () => buildAdminAccessAuditSummary(adminUsers),
    [adminUsers],
  );
  const enabledClaimCount = useMemo(
    () => countEnabledClaimValues(adminUsers, CLAIM_KEY),
    [adminUsers],
  );
  const falseValuedClaimCount = adminUsers.length - enabledClaimCount;
  const metadataItems = useMemo(
    () => getMetadataItems(data, adminUsers.length),
    [adminUsers.length, data],
  );

  useEffect(() => {
    logger.debug("ADMIN_ACCESS_AUDIT_ROUTE_ENTER", {
      component: "AdminAccessAuditRoute",
      feature: "ADMIN",
      operation: "ADMIN_ACCESS_AUDIT_ROUTE_ENTER",
    });
    pageStartedAtRef.current = Date.now();
    readyLoggedRef.current = false;
  }, []);

  useEffect(() => {
    if (readyLoggedRef.current || isLoading || isFetching || error) {
      return;
    }

    readyLoggedRef.current = true;
    logger.debug("ADMIN_ACCESS_AUDIT_ROUTE_READY", {
      component: "AdminAccessAuditRoute",
      feature: "ADMIN",
      operation: "ADMIN_ACCESS_AUDIT_ROUTE_READY",
      adminUserCount: adminUsers.length,
      disabledClaimValueCount: falseValuedClaimCount,
      disabledCount: summary.disabled,
      enabledClaimCount,
      verifiedCount: summary.verified,
      pageLoadDurationMs:
        pageStartedAtRef.current === null
          ? null
          : Date.now() - pageStartedAtRef.current,
    });
  }, [
    adminUsers.length,
    enabledClaimCount,
    error,
    falseValuedClaimCount,
    isFetching,
    isLoading,
    summary.disabled,
    summary.verified,
  ]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const details = getPublicErrorDetails(
      error,
      "Failed to load admin access audit.",
    );
    logger.error(
      "ADMIN_ACCESS_AUDIT_ROUTE_FAIL",
      toError(error) ?? new Error("admin_access_audit_route_failed"),
      {
        component: "AdminAccessAuditRoute",
        feature: "ADMIN",
        operation: "ADMIN_ACCESS_AUDIT_ROUTE_FAIL",
        errorCode: details.errorCode,
        correlationId: details.correlationId ?? undefined,
      },
    );
  }, [error]);

  return (
    <SettingsWorkspaceChrome
      actions={
        <div className="admin-access-audit__toolbar">
          <VNextButton
            disabled={isFetching}
            onClick={() => {
              logger.debug("ADMIN_ACCESS_AUDIT_ROUTE_REFRESH", {
                component: "AdminAccessAuditRoute",
                feature: "ADMIN",
                operation: "ADMIN_ACCESS_AUDIT_ROUTE_REFRESH",
              });
              void refetch();
            }}
            tone="secondary"
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </VNextButton>
        </div>
      }
      subtitle="Review every Firebase user where the admin claim key exists, including false values that may need cleanup, and compare it against the linked app record."
      title="Admin access audit"
    >
      <div className="vnext-stack">
        {error ? (
          <VNextNotice
            action={
              <VNextButton onClick={() => void refetch()} tone="quiet">
                Retry
              </VNextButton>
            }
            tone="danger"
          >
            Failed to load admin access audit.
          </VNextNotice>
        ) : null}

        {falseValuedClaimCount > 0 ? (
          <VNextNotice tone="warning">
            {falseValuedClaimCount === 1
              ? "1 Firebase user still carries the admin claim key with the value set to false. It stays in this audit so operators can spot stale or inconsistent claim state."
              : `${falseValuedClaimCount} Firebase users still carry the admin claim key with the value set to false. They stay in this audit so operators can spot stale or inconsistent claim state.`}
          </VNextNotice>
        ) : null}

        <div className="vnext-grid vnext-grid--two">
          <VNextCard title="Summary">
            <div className="vnext-stats-grid">
              <VNextStat
                detail="Firebase users where the admin claim key exists."
                label="Claim key present"
                value={summary.total.toLocaleString()}
              />
              <VNextStat
                detail="Users where the admin claim is explicitly true."
                label="Claim enabled"
                value={enabledClaimCount.toLocaleString()}
              />
              <VNextStat
                detail="Accounts with verified email addresses."
                label="Email verified"
                value={summary.verified.toLocaleString()}
              />
              <VNextStat
                detail="Admin accounts currently disabled in Firebase."
                label="Disabled accounts"
                value={summary.disabled.toLocaleString()}
              />
            </div>
          </VNextCard>

          <VNextCard title="Claim scan metadata">
            <div className="admin-access-audit__meta">
              <VNextKeyValueList items={metadataItems} />
            </div>
          </VNextCard>
        </div>

        <VNextCard
          subtitle="Includes linked app users and Firebase-only admin claims so cleanup does not miss orphaned access."
          title="Accounts in scope"
        >
          {isLoading ? (
            <div className="vnext-empty">Loading admin access audit…</div>
          ) : (
            <AdminAccessAuditResultsTable
              claimKey={CLAIM_KEY}
              users={adminUsers}
            />
          )}
        </VNextCard>
      </div>
    </SettingsWorkspaceChrome>
  );
}
